import { useEffect, useState } from 'react';
import { message } from '../utils/antdMessage';
import { apiFetch } from '../utils/api';
import { hasPermission, getCurrentUserId } from '../utils/permissionControl';
import { saveRescueTargetsToStorage, saveRescueTracksToStorage, removeLocalTrackLayers } from '../utils/rescuePush';

/**
 * 历史查询 / 完结 / 删除 / 飞行定位
 */
export default function useRescuePushHistory({
  activeTab,
  mapRef,
  rescueTargetMarkersRef,
  trackLinesRef,
  setPersistentTargets,
  setPersistentTracks
}) {
  const [historyTab, setHistoryTab] = useState('target');
  const [historyMeta, setHistoryMeta] = useState({
    viewScope: 'own',
    canDeleteAll: false,
    currentUserId: getCurrentUserId()
  });
  const [targetHistory, setTargetHistory] = useState([]);
  const [trackHistory, setTrackHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const canDeleteAllHistory = hasPermission('救援推送历史删除') || historyMeta.canDeleteAll === true;

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      if (historyTab === 'target') {
        const response = await apiFetch('/api/rescue/target?include_finished=true');
        const data = await response.json();
        if (data.success) {
          setTargetHistory(data.targets || []);
          if (data.meta) setHistoryMeta(data.meta);
        } else {
          setTargetHistory([]);
          message.error(data.error || '获取目标历史失败');
        }
      } else {
        const response = await apiFetch('/api/rescue/track?include_finished=true');
        const data = await response.json();
        if (data.success) {
          setTrackHistory(data.tracks || []);
          if (data.meta) setHistoryMeta(data.meta);
        } else {
          setTrackHistory([]);
          message.error(data.error || '获取轨迹历史失败');
        }
      }
    } catch (error) {
      console.error('获取历史数据失败:', error);
      message.error('获取历史数据失败');
    }
    setHistoryLoading(false);
  };

  useEffect(() => {
    if (activeTab === 'history') fetchHistory();
  }, [activeTab, historyTab]);

  const canDeleteHistoryItem = (item) => {
    if (canDeleteAllHistory) return true;
    const uid = historyMeta.currentUserId ?? getCurrentUserId();
    if (uid == null || item?.operator_id == null) return false;
    return Number(item.operator_id) === Number(uid);
  };

  const handleToggleFinished = async (type, item, finished) => {
    try {
      const url = type === 'target' ? `/api/rescue/target/${item.id}/finished` : `/api/rescue/track/${item.id}/finished`;
      const response = await apiFetch(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ finished })
      });
      const data = await response.json();
      if (!data.success) {
        message.error(data.error || '更新完结状态失败');
        return;
      }

      const nextStatus = finished ? '已完结' : '已发送';
      if (type === 'target') {
        setTargetHistory((prev) =>
          prev.map((row) => (row.id === item.id ? { ...row, status: nextStatus } : row))
        );
      } else {
        setTrackHistory((prev) =>
          prev.map((row) => (row.id === item.id ? { ...row, status: nextStatus } : row))
        );
      }
      message.success(finished ? '已标记为完结' : '已取消完结');
    } catch (error) {
      console.error('更新完结状态失败:', error);
      message.error('更新完结状态失败');
    }
  };

  const handleHistoryItemClick = (type, item) => {
    if (!mapRef.current) return;

    console.log('点击历史记录:', type, item);

    let targetLng;
    let targetLat;

    if (type === 'target') {
      if (item.lng && item.lat) {
        targetLng = parseFloat(item.lng);
        targetLat = parseFloat(item.lat);
      } else {
        console.warn('目标记录缺少坐标信息:', item);
        return;
      }
    } else if (type === 'track') {
      if (item.coordinates && item.coordinates.length > 0) {
        const firstCoord = item.coordinates[0];
        targetLng = parseFloat(firstCoord.lng);
        targetLat = parseFloat(firstCoord.lat);
      } else {
        console.warn('轨迹记录缺少坐标信息:', item);
        return;
      }
    }

    if (targetLng && targetLat && !isNaN(targetLng) && !isNaN(targetLat)) {
      const targetZoom = 16.72;

      mapRef.current.flyTo({
        center: [targetLng, targetLat],
        zoom: targetZoom,
        duration: 2000,
        essential: true
      });

      console.log(`飞行到${type === 'target' ? '目标' : '轨迹'}位置:`, [targetLng, targetLat], '缩放级别:', targetZoom);
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('确定永久删除？目标历史与地图同坐标指令点将清除；操作写入审计日志。')) return;
    try {
      if (type === 'target') {
        const response = await apiFetch(`/api/rescue/target/${id}`, { method: 'DELETE' });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.success === false) {
          message.error(data.error || '删除失败');
          return;
        }
        if (Array.isArray(data.hidden_command_batch_ids) && data.hidden_command_batch_ids.length) {
          message.success(`已永久删除，并清除地图指令点 ${data.hidden_command_batch_ids.length} 批`);
        } else {
          message.success('已永久删除');
        }

        const markerToRemove = rescueTargetMarkersRef.current.find((m) => m.id === `target-${id}`);
        if (markerToRemove) {
          markerToRemove.marker.remove();
          rescueTargetMarkersRef.current = rescueTargetMarkersRef.current.filter((m) => m.id !== `target-${id}`);
        }

        setPersistentTargets((prev) => {
          const newTargets = prev.filter((t) => String(t.id) !== String(id));
          saveRescueTargetsToStorage(newTargets);
          return newTargets;
        });

        fetchHistory();
      } else {
        const response = await apiFetch(`/api/rescue/track/${id}`, { method: 'DELETE' });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.success === false) {
          message.error(data.error || '删除失败');
          return;
        }

        const trackId = `track-${id}`;
        if (mapRef.current) {
          try {
            removeLocalTrackLayers(mapRef.current, trackId);
          } catch (e) {
            console.log('删除轨迹图层失败:', e);
          }
        }
        if (trackLinesRef?.current) {
          const entry = trackLinesRef.current.find((t) => String(t?.id) === String(trackId));
          (entry?.markers || []).forEach((marker) => {
            try {
              marker?.remove?.();
            } catch (_) {
              /* ignore */
            }
          });
          trackLinesRef.current = trackLinesRef.current.filter((t) => String(t?.id) !== String(trackId));
        }

        setPersistentTracks((prev) => {
          const newTracks = prev.filter((t) => String(t.id) !== String(id));
          saveRescueTracksToStorage(newTracks);
          return newTracks;
        });

        fetchHistory();
      }
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除失败');
    }
  };

  return {
    historyTab,
    setHistoryTab,
    historyMeta,
    targetHistory,
    trackHistory,
    historyLoading,
    canDeleteHistoryItem,
    handleToggleFinished,
    handleHistoryItemClick,
    handleDelete,
    fetchHistory
  };
}
